import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

/**
 * Central (dashboard-managed) POS favorites. The dashboard API is a thin
 * passthrough to pos-backend, which is the source of truth — POS devices sync
 * these down and merge them with each register's own local tiles.
 *
 * Scope: an empty `locationId` means the tenant-wide set (applies to every
 * location); a set `locationId` means that one location's set. A device sees
 * BOTH its location set and the tenant-wide set (most-specific-wins on ties).
 */
export interface CentralFavorite {
  id?: string;
  type: 'product' | 'department' | 'category' | 'action';
  reference_id: string;
  label?: string | null;
  icon_name?: string | null;
  color_hex?: string | null;
  sort_order: number;
}

/**
 * GET the central favorites for a scope. `locationId` empty = tenant-wide;
 * `registerId` (optional) scopes to a single register at that location.
 */
export function usePosFavorites(locationId: string, registerId = '') {
  return useQuery<{ data: CentralFavorite[] }>({
    queryKey: ['pos-favorites', locationId ?? '', registerId ?? ''],
    queryFn: () =>
      api.get(
        `/pos/favorites?location_id=${encodeURIComponent(locationId ?? '')}` +
          `&register_id=${encodeURIComponent(registerId ?? '')}`,
      ),
  });
}

/** Replace the central favorites for a scope (full-replace so removals propagate). */
export function useUpdatePosFavorites() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { locationId: string | null; registerId?: string | null; favorites: CentralFavorite[] }) =>
      api.put('/pos/favorites', payload),
    onSuccess: (_res, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['pos-favorites', variables.locationId ?? '', variables.registerId ?? ''],
      });
    },
  });
}
